import React, { useState, useEffect } from 'react';
import {
  uploadFile,
  getFileUrl,
  createFollowUpSequence,
  getFollowUpSequence,
  updateFollowUpSequence,
  deleteFile,
  getFollowUpScheduleConfig,
  createFollowUpScheduleConfig,
  updateFollowUpScheduleConfig,
  deleteFollowUpScheduleConfig,
  listWhatsAppTemplates,
  WhatsAppTemplate,
} from '../services/api.ts';
import {
  Trash2,
  Edit2,
  Plus,
  Play,
  Calendar,
  Clock,
  MessageCircle,
  Check,
  X,
  Image,
  Video,
  Music,
  Type,
  ChevronDown,
  ChevronUp,
  File,
  Settings,
  AlertCircle,
  Save,
  ChevronRight,
  Search,
  Filter,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  analyzeTemplateSupport,
  buildTemplateMessageConfig,
  getTemplateDisplayName,
  getTemplateParameterSummary,
  getTemplateVariableOption,
  getTemplateVariableOptions,
  isTemplateSupportedForAutomation,
  parseStoredTemplateMessage,
  serializeTemplateMessage,
  TemplateMessageConfig,
} from '../utils/whatsappTemplateVariables.ts';

// ---------------------------
// 1) Tipos internos do Front
// ---------------------------
interface MensagemLocal {
  id?: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'template';
  content: string | File | any; // usando any para evitar problemas com instanceof
}

interface PassoLocal {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  messages: MensagemLocal[];
}

// ---------------------------
// 2) Tipos para envio ao backend
// ---------------------------
interface MensagemAPI {
  id?: number;
  type: string;
  content: string;
}

interface PassoAPI {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: string;
  messages: MensagemAPI[];
}

// Schedule
interface ScheduleDay {
  enabled: boolean;
  start: string;
  end: string;
}

interface ScheduleData {
  [key: string]: ScheduleDay;
}

// ------------------------------------
// Constantes e limitadores
// ------------------------------------
const MAX_PASSOS = 10; // Aumentado para 10 passos (você pode definir um valor maior ou remover completamente a limitação)
const MAX_MENSAGENS_POR_PASSO = 3;

const LIMITE_ARQUIVOS = {
  image: 2 * 1024 * 1024,   // 2MB
  audio: 5 * 1024 * 1024,  // 5MB
  video: 5 * 1024 * 1024,  // 5MB
};

const daysOfWeek = [
  { label: 'Segunda', key: 'monday' },
  { label: 'Terça', key: 'tuesday' },
  { label: 'Quarta', key: 'wednesday' },
  { label: 'Quinta', key: 'thursday' },
  { label: 'Sexta', key: 'friday' },
  { label: 'Sábado', key: 'saturday' },
  { label: 'Domingo', key: 'sunday' },
];

const timeUnits = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Dias' }
];

const messageTypes = [
  { value: 'text', label: 'Texto', icon: Type },
  { value: 'template', label: 'Template Meta', icon: MessageCircle },
  { value: 'image', label: 'Imagem', icon: Image },
  { value: 'audio', label: 'Áudio', icon: Music },
  { value: 'video', label: 'Vídeo', icon: Video }
];

// ------------------------------------
// Função auxiliar para mesclar config
// ------------------------------------
function mergeScheduleData(
  serverData: Record<string, any>,
  baseData: ScheduleData
): ScheduleData {
  const merged: ScheduleData = { ...baseData };

  for (const dayKey in merged) {
    const fromServer = serverData[dayKey];

    if (fromServer === null) {
      merged[dayKey] = {
        enabled: false,
        start: merged[dayKey].start,
        end: merged[dayKey].end,
      };

    } else if (typeof fromServer === 'object') {
      merged[dayKey] = {
        enabled: fromServer.enabled ?? true,
        start: fromServer.start ?? merged[dayKey].start,
        end: fromServer.end ?? merged[dayKey].end,
      };
    }
  }

  return merged;
}

// ------------------------------------
// Função para verificar se é um arquivo
// ------------------------------------
function isFile(value: any): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    'size' in value &&
    'type' in value
  );
}

function followUpValidationDetail(error: unknown): string | undefined {
  const response = (error as any)?.response;
  if (response?.status !== 422) return undefined;

  const detail = response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (!Array.isArray(detail)) return undefined;

  const messages = detail.map((item: any) => {
    if (typeof item === 'string') return item;
    if (!item || typeof item !== 'object') return '';

    const message = typeof item.msg === 'string' ? item.msg : '';
    const location = Array.isArray(item.loc)
      ? item.loc.filter((part: unknown) => part !== 'body').join('.')
      : '';
    return [location, message || JSON.stringify(item)].filter(Boolean).join(': ');
  }).filter(Boolean);

  return messages.length > 0 ? messages.join('; ') : undefined;
}

// ------------------------------------
// Preview simples de arquivo (imagem/áudio/vídeo)
// ------------------------------------
const PreviewArquivo = ({ tipo, conteudo }: { tipo: string; conteudo: string | File | any }) => {
  if (isFile(conteudo)) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 rounded-md my-2">
        <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          Arquivo pendente de upload. Será enviado ao salvar as configurações.
        </p>
      </div>
    );
  }

  if (typeof conteudo !== 'string') {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 rounded-md my-2">
        <p className="text-sm text-red-700 dark:text-red-400 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          Formato de arquivo inválido
        </p>
      </div>
    );
  }

  const clinicId = Number(localStorage.getItem('clinic_id'));
  const clientId = Number(localStorage.getItem('client_id'));
  const fileName = conteudo.split('/').pop() || '';
  const url = getFileUrl(clinicId, clientId, fileName);

  if (tipo === 'image') {
    return (
      <div className="mt-2 rounded-lg overflow-hidden">
        <img
          src={url}
          alt="Preview"
          className="w-full max-h-48 object-cover rounded-lg"
        />
      </div>
    );
  }
  if (tipo === 'audio') {
    return (
      <div className="bg-slate-50 dark:bg-gray-800 p-3 rounded-lg mt-2 border border-slate-200 dark:border-gray-700">
        <audio controls className="w-full">
          <source src={url} type="audio/mpeg" />
          Seu navegador não suporta o elemento de áudio.
        </audio>
      </div>
    );
  }
  if (tipo === 'video') {
    return (
      <div className="mt-2 rounded-lg overflow-hidden">
        <video controls className="w-full max-h-48 rounded-lg">
          <source src={url} type="video/mp4" />
          Seu navegador não suporta o elemento de vídeo.
        </video>
      </div>
    );
  }
  return null;
};


// Componente para visualizar uma mensagem no modo de leitura
const MessagePreview = ({ message }: { message: MensagemLocal }) => {
  const { type, content } = message;

  if (type === 'template') {
    const templateConfig = parseStoredTemplateMessage(content);
    const templateSummary = getTemplateParameterSummary(templateConfig, 'follow_up');
    return (
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sky-900 dark:border-sky-900/50 dark:bg-sky-900/20 dark:text-sky-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-300">
          Template Meta
        </p>
        <p className="mt-1 text-sm">{getTemplateDisplayName(content) || 'Template sem nome'}</p>
        {templateSummary.length > 0 && (
          <div className="mt-2 space-y-1">
            {templateSummary.map((summaryLine) => (
              <p key={summaryLine} className="text-xs opacity-80">
                {summaryLine}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === 'text' && typeof content === 'string') {
    return <p className="text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-800 border border-slate-100 dark:border-gray-700 p-3 rounded-lg">{content}</p>;
  }


  return <PreviewArquivo tipo={type} conteudo={content} />;
};

// Timeline indicator component
const TimelineIndicator = ({ passos, onEditStep, onAddStep }: {
  passos: PassoLocal[],
  onEditStep: (stepNumber: number) => void,
  onAddStep: () => void
}) => {
  const { isDark } = useTheme();

  return (
    <div className="relative mt-8 mb-6">
      <div className={`absolute top-0 bottom-0 left-8 w-0.5 ${isDark ? 'bg-gray-700' : 'bg-slate-200'}`}></div>
      {passos.map((step) => {
        const unidadeTexto = step.send_after_unit === 'days' ? 'dias' :
          step.send_after_unit === 'hours' ? 'horas' : 'minutos';

        return (
          <div key={step.step_number} className="relative flex items-center mb-12">
            <div className={`absolute left-8 transform -translate-x-1/2 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold z-10 text-white bg-sminy-primary`}>
              {step.step_number}
            </div>
            <div className="ml-12 flex-1">
              <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Após {step.send_after} {unidadeTexto}
              </div>
              <div className={`font-semibold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                <MessageCircle className="w-4 h-4" />
                {step.messages.length} {step.messages.length === 1 ? 'mensagem' : 'mensagens'}
              </div>
              <button
                onClick={() => onEditStep(step.step_number)}
                className={`mt-2 text-sm flex items-center transition-colors ${isDark ? 'text-sminy-primary hover:text-sminy-primary/80' : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Editar passo
              </button>
            </div>
          </div>
        );
      })}

      {/* Add step button at the end of timeline */}
      {passos.length < MAX_PASSOS && (
        <div className="relative flex items-center">
          <div className={`absolute left-8 transform -translate-x-1/2 h-6 w-6 rounded-full border-2 border-dashed flex items-center justify-center z-10 ${isDark ? 'border-gray-600 text-gray-400' : 'border-slate-400 text-slate-500'
            }`}>
            <Plus className="w-3 h-3" />
          </div>
          <button
            onClick={onAddStep}
            className={`ml-12 font-medium flex items-center gap-2 transition-colors ${isDark ? 'text-sminy-primary hover:text-sminy-primary/80' : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            <Plus className="w-4 h-4" />
            Adicionar Passo
          </button>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------
// Componente principal de FollowUp
// -------------------------------------------------------------
const FollowUp: React.FC = () => {
  const { isDark } = useTheme();
  const [passos, setPassos] = useState<PassoLocal[]>([]);
  const [editandoPasso, setEditandoPasso] = useState<PassoLocal | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<WhatsAppTemplate[]>([]);
  const [unsupportedTemplateCount, setUnsupportedTemplateCount] = useState(0);

  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [hasSequence, setHasSequence] = useState(false);
  const [activeTab, setActiveTab] = useState('sequence'); // 'sequence' ou 'schedule'
  const [savedStatus, setSavedStatus] = useState<string | null>(null); // null, 'saving', 'saved', 'error'

  // schedule
  const [scheduleExists, setScheduleExists] = useState(false);
  const [scheduleEditMode, setScheduleEditMode] = useState(false);

  const [schedule, setSchedule] = useState<ScheduleData>({
    monday: { enabled: false, start: '08:00', end: '18:00' },
    tuesday: { enabled: false, start: '08:00', end: '18:00' },
    wednesday: { enabled: false, start: '08:00', end: '18:00' },
    thursday: { enabled: false, start: '08:00', end: '18:00' },
    friday: { enabled: false, start: '08:00', end: '18:00' },
    saturday: { enabled: false, start: '08:00', end: '14:00' },
    sunday: { enabled: false, start: '00:00', end: '00:00' },
  });

  const getTemplateByName = (templateName: string): WhatsAppTemplate | undefined => {
    return availableTemplates.find((template) => template.name === templateName);
  };

  const normalizeTemplateContent = (
    content: MensagemLocal['content'],
    fallbackTemplateName?: string,
  ): TemplateMessageConfig | null => {
    const parsedTemplate = parseStoredTemplateMessage(content);
    const templateName = parsedTemplate?.template_name || fallbackTemplateName || '';
    if (!templateName) {
      return null;
    }

    const selectedTemplate = getTemplateByName(templateName);
    if (!selectedTemplate) {
      return parsedTemplate;
    }

    return buildTemplateMessageConfig(selectedTemplate, 'follow_up', parsedTemplate);
  };

  // Ao montar, carrega configuração e schedule
  useEffect(() => {
    const clinicIdStr = localStorage.getItem('clinic_id');
    if (!clinicIdStr) return;
    const clinicIdNum = Number(clinicIdStr);
    if (!clinicIdNum) return;

    carregarConfigExistente(clinicIdNum);
    carregarScheduleConfig(clinicIdNum);
    carregarTemplatesFollowUp(clinicIdNum);
  }, []);

  // Limpa mensagens de erro/sucesso após tempo
  useEffect(() => {
    if (erro || sucesso) {
      const timer = setTimeout(() => {
        setErro(null);
        setSucesso(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [erro, sucesso]);

  // -----------------------------------------------------------
  // Carrega config de FollowUp
  // -----------------------------------------------------------
  const carregarConfigExistente = async (clinicId: number) => {
    try {
      setCarregando(true);
      const seq = await getFollowUpSequence(clinicId);

      if (seq === null) {
        setHasSequence(false);
        setPassos([]);
      } else {
        setHasSequence(true);

        // Converter steps e guardar ID de step e mensagens
        const passosConvertidos: PassoLocal[] = seq.steps.map((step: any) => ({
          id: step.id,
          step_number: step.step_number,
          send_after: step.send_after,
          send_after_unit: step.send_after_unit as 'days' | 'hours' | 'minutes',
          messages: step.messages.map((msg: any) => ({
            id: msg.id,
            type: msg.type as 'text' | 'image' | 'audio' | 'video' | 'template',
            content: msg.type === 'template'
              ? (parseStoredTemplateMessage(msg.content) || msg.content)
              : msg.content,
          })),
        }));

        // Ordenamos os passos pelo ID ascendente
        passosConvertidos.sort((a, b) => {
          const aId = a.id ?? 0;
          const bId = b.id ?? 0;
          return aId - bId;
        });

        setPassos(passosConvertidos);
      }
    } catch (error: any) {
      setErro('Erro ao carregar configuração existente');
      console.error(error);
    } finally {
      setCarregando(false);
    }
  };

  const carregarTemplatesFollowUp = async (clinicId: number) => {
    try {
      const templates = await listWhatsAppTemplates(clinicId, 'APPROVED', 'follow_up');
      const supportedTemplates = templates.filter((template) => isTemplateSupportedForAutomation(template));
      setAvailableTemplates(supportedTemplates);
      setUnsupportedTemplateCount(templates.length - supportedTemplates.length);
    } catch (error) {
      console.error('Erro ao carregar templates de follow-up:', error);
      setAvailableTemplates([]);
      setUnsupportedTemplateCount(0);
    }
  };

  // -----------------------------------------------------------
  // Carrega config de horários
  // -----------------------------------------------------------
  const carregarScheduleConfig = async (clinicId: number) => {
    try {
      const resp = await getFollowUpScheduleConfig(clinicId);
      if (resp) {
        // Mesclar
        const mergedData = mergeScheduleData(resp.schedule_data, schedule);
        setSchedule(mergedData);
        setScheduleExists(true);
        setScheduleEditMode(false);
      }
    } catch (error) {
      console.log('Nenhuma config de horários encontrada ou erro:', error);
      setScheduleExists(false);
    }
  };

  // -----------------------------------------------------------
  // Salvar config de horários
  // -----------------------------------------------------------
  const handleSaveScheduleConfig = async () => {
    try {
      setSavedStatus('saving');
      const clinicIdStr = localStorage.getItem('clinic_id');
      if (!clinicIdStr) throw new Error('clinic_id não encontrado');
      const clinicIdNum = Number(clinicIdStr);

      // Filtrar apenas dias enabled
      const filteredSchedule: ScheduleData = {};
      for (const dayKey in schedule) {
        const dayData = schedule[dayKey];
        if (dayData.enabled) {
          filteredSchedule[dayKey] = {
            enabled: true,
            start: dayData.start,
            end: dayData.end,
          };
        }
      }

      const schedulePayload = { schedule_data: filteredSchedule };

      if (!scheduleExists) {
        await createFollowUpScheduleConfig(clinicIdNum, schedulePayload);
        setScheduleExists(true);
      } else {
        await updateFollowUpScheduleConfig(clinicIdNum, schedulePayload);
      }

      setScheduleEditMode(false);
      setSavedStatus('saved');
      setTimeout(() => setSavedStatus(null), 2000);
    } catch (error) {
      console.error('Erro ao salvar config de horários:', error);
      setSavedStatus('error');
    }
  };

  // -----------------------------------------------------------
  // Deletar config de horários
  // -----------------------------------------------------------
  const handleDeleteScheduleConfig = async () => {
    try {
      setCarregando(true);
      const clinicIdStr = localStorage.getItem('clinic_id');
      if (!clinicIdStr) throw new Error('clinic_id não encontrado');
      const clinicIdNum = Number(clinicIdStr);

      await deleteFollowUpScheduleConfig(clinicIdNum);
      setScheduleExists(false);
      setScheduleEditMode(false);

      // Reset local
      setSchedule({
        monday: { enabled: false, start: '08:00', end: '18:00' },
        tuesday: { enabled: false, start: '08:00', end: '18:00' },
        wednesday: { enabled: false, start: '08:00', end: '18:00' },
        thursday: { enabled: false, start: '08:00', end: '18:00' },
        friday: { enabled: false, start: '08:00', end: '18:00' },
        saturday: { enabled: false, start: '08:00', end: '14:00' },
        sunday: { enabled: false, start: '00:00', end: '00:00' },
      });

      setSucesso('Config de horários deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar config de horários:', error);
      setErro('Falha ao deletar config de horários');
    } finally {
      setCarregando(false);
    }
  };

  // -----------------------------------------------------------
  // Salvar config (faz upload dos arquivos e manda pro backend)
  // -----------------------------------------------------------
  const salvarConfig = async () => {
    const passoSemMensagem = passos.find((passo) =>
      !passo.messages.some((mensagem) => {
        if (typeof mensagem.content === 'string') return mensagem.content.trim().length > 0;
        if (isFile(mensagem.content)) return mensagem.content.size > 0;
        return (
          mensagem.type === 'template' &&
          typeof mensagem.content === 'object' &&
          mensagem.content !== null &&
          typeof mensagem.content.template_name === 'string' &&
          mensagem.content.template_name.trim().length > 0
        );
      }),
    );
    if (passoSemMensagem) {
      setErro(`Passo ${passoSemMensagem.step_number} está sem mensagem`);
      setSavedStatus('error');
      return;
    }

    try {
      setSavedStatus('saving');

      const clinicIdStr = localStorage.getItem('clinic_id');
      const clientIdStr = localStorage.getItem('client_id');
      if (!clinicIdStr || !clientIdStr) {
        throw new Error('ID da clínica ou cliente não encontrado');
      }

      const clinicIdNum = Number(clinicIdStr);

      // 1) Transformar PassoLocal[] em PassoAPI[] (fazendo upload se houver File)
      const passosAPI: PassoAPI[] = await Promise.all(
        passos.map(async (passoLocal) => {
          // Ordenamos as mensagens pelo ID
          const messagesSorted = passoLocal.messages
            .slice()
            .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

          const messagesAPI = await Promise.all(
            messagesSorted.map(async (msgLocal) => {
              // Verificamos se é um arquivo usando nossa função isFile
              if (['image', 'audio', 'video'].includes(msgLocal.type) && isFile(msgLocal.content)) {
                // upload
                const file = msgLocal.content as File;
                const limiteArquivo = LIMITE_ARQUIVOS[msgLocal.type];
                if (file.size > limiteArquivo) {
                  throw new Error(
                    `Arquivo muito grande. Máx p/ ${msgLocal.type}: ${limiteArquivo / (1024 * 1024)}MB`
                  );
                }
                const resultadoUpload = await uploadFile(file);
                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: resultadoUpload.path,
                } as MensagemAPI;
              } else if (msgLocal.type === 'template') {
                const templateConfig = normalizeTemplateContent(msgLocal.content);
                if (!templateConfig?.template_name) {
                  throw new Error('Selecione um template Meta aprovado para todas as mensagens do tipo template.');
                }

                const hasInvalidParameter = templateConfig.components.some((component) =>
                  (component.parameters || []).some((parameter) => {
                    if (!parameter?.source) {
                      return true;
                    }
                    if (parameter.source === 'literal') {
                      return !parameter.value?.trim();
                    }
                    return !getTemplateVariableOption(parameter.source, 'follow_up');
                  }),
                );

                if (hasInvalidParameter) {
                  throw new Error('Preencha todas as variáveis do template antes de salvar o passo.');
                }

                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: serializeTemplateMessage(templateConfig),
                } as MensagemAPI;
              } else if (typeof msgLocal.content === 'string') {
                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: msgLocal.content,
                } as MensagemAPI;
              } else {
                throw new Error('Tipo de conteúdo inesperado.');
              }
            })
          );

          return {
            id: passoLocal.id,
            step_number: passoLocal.step_number,
            send_after: passoLocal.send_after,
            send_after_unit: passoLocal.send_after_unit,
            messages: messagesAPI,
          } as PassoAPI;
        })
      );

      // 2) Montamos o payload final
      const payload = {
        clinic_id: clinicIdNum,
        client_id: clientIdStr,
        name: 'Sequência de Follow-up',
        description: 'Mensagens automáticas de follow-up',
        steps: passosAPI,
      };

      // 3) create ou update
      if (hasSequence) {
        await updateFollowUpSequence(clinicIdNum, payload);
        setSavedStatus('saved');
      } else {
        await createFollowUpSequence(clinicIdNum, payload);
        setHasSequence(true);
        setSavedStatus('saved');
      }

      setTimeout(() => setSavedStatus(null), 2000);

      // 4) Recarrega do GET para atualizar e ordenar local
      await carregarConfigExistente(clinicIdNum);
      setEditandoPasso(null);
    } catch (err) {
      setErro(followUpValidationDetail(err) || 'Falha ao salvar configuração');
      setSavedStatus('error');
      console.error(err);
    }
  };

  // -----------------------------------------------------------
  // Adicionar passo / editar passo / adicionar mensagens
  // -----------------------------------------------------------
  const adicionarPasso = () => {
    if (passos.length >= MAX_PASSOS) {
      setErro(`Número máximo de passos (${MAX_PASSOS}) atingido`);
      return;
    }
    const novoPasso: PassoLocal = {
      step_number: passos.length + 1,
      send_after: 1,
      send_after_unit: 'days',
      messages: [],
    };
    setPassos([...passos, novoPasso]);
    setEditandoPasso(novoPasso);
  };

  const editarPasso = (numeroPasso: number) => {
    const passo = passos.find(p => p.step_number === numeroPasso);
    if (passo) {
      // Ordena as mensagens pelo ID ao editar um passo
      const passoOrdenado = {
        ...passo,
        messages: [...passo.messages].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      };
      setEditandoPasso(passoOrdenado);
    }
  };

  const atualizarPasso = (passoAtualizado: PassoLocal) => {
    setPassos((prev) =>
      prev.map((p) => (p.step_number === passoAtualizado.step_number ? passoAtualizado : p))
    );
    setEditandoPasso(passoAtualizado);
  };

  const fecharEdicaoPasso = () => {
    setEditandoPasso(null);
  };

  const adicionarMensagem = (numeroPasso: number) => {
    const passoAlvo = passos.find((p) => p.step_number === numeroPasso);
    if (!passoAlvo) return;
    if (passoAlvo.messages.length >= MAX_MENSAGENS_POR_PASSO) {
      setErro(`Número máximo de mensagens por passo (${MAX_MENSAGENS_POR_PASSO}) atingido`);
      return;
    }

    const novaMensagem: MensagemLocal = { type: 'text', content: '' };

    // Ordenar as mensagens existentes por ID
    const mensagensOrdenadas = [...passoAlvo.messages].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

    const novoPassoComMensagem: PassoLocal = {
      ...passoAlvo,
      messages: [...mensagensOrdenadas, novaMensagem],
    };

    setPassos((prev) =>
      prev.map((p) => (p.step_number === numeroPasso ? novoPassoComMensagem : p))
    );

    setEditandoPasso(novoPassoComMensagem);
  };

  const deletarMensagem = async (numeroPasso: number, indiceMensagem: number) => {
    try {
      const passoIndex = passos.findIndex((p) => p.step_number === numeroPasso);
      if (passoIndex === -1) return;
      const passo = passos[passoIndex];

      // Ordenar mensagens por ID antes de acessar a mensagem pelo índice
      const mensagensOrdenadas = [...passo.messages].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      const mensagem = mensagensOrdenadas[indiceMensagem];

      // Se houver ID e content for string (arquivo salvo), podemos deletar do servidor
      if (mensagem && mensagem.type !== 'text' && typeof mensagem.content === 'string') {
        const fileName = mensagem.content.split('/').pop();
        if (fileName) {
          try {
            await deleteFile(fileName);
          } catch (error) {
            console.warn(`Não foi possível deletar arquivo ${fileName}:`, error);
          }
        }
      }

      // Remover a mensagem da lista ordenada
      const mensagensAtualizadas = [...mensagensOrdenadas];
      mensagensAtualizadas.splice(indiceMensagem, 1);

      const passoAtualizado = { ...passo, messages: mensagensAtualizadas };

      setPassos((prev) =>
        prev.map((p) => (p.step_number === numeroPasso ? passoAtualizado : p))
      );

      setEditandoPasso(passoAtualizado);
    } catch (err) {
      setErro('Falha ao deletar mensagem');
      console.error(err);
    }
  };

  const alterarMensagem = (
    numeroPasso: number,
    indiceMensagem: number,
    messageType: 'text' | 'image' | 'audio' | 'video' | 'template',
    conteudo: string | File | any
  ) => {
    const passoIndex = passos.findIndex((p) => p.step_number === numeroPasso);
    if (passoIndex === -1) return;

    const passo = passos[passoIndex];

    // Ordenar mensagens por ID e depois fazer a alteração no índice correto
    const mensagensOrdenadas = [...passo.messages].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    const oldMsg = mensagensOrdenadas[indiceMensagem];

    const updatedMsg: MensagemLocal = {
      id: oldMsg?.id,
      type: messageType,
      content: conteudo,
    };
    mensagensOrdenadas[indiceMensagem] = updatedMsg;

    const passoAtualizado = { ...passo, messages: mensagensOrdenadas };

    setPassos((prev) =>
      prev.map((p) => (p.step_number === numeroPasso ? passoAtualizado : p))
    );

    setEditandoPasso(passoAtualizado);
  };

  const unidadeTempoTexto = (unidade: string) => {
    switch (unidade) {
      case 'days':
        return 'dias';
      case 'hours':
        return 'horas';
      case 'minutes':
        return 'minutos';
      default:
        return unidade;
    }
  };

  const renderIconForMessageType = (type: string) => {
    switch (type) {
      case 'text':
        return <Type className="w-4 h-4 text-slate-500" />;
      case 'template':
        return <MessageCircle className="w-4 h-4 text-slate-500" />;
      case 'image':
        return <Image className="w-4 h-4 text-slate-500" />;
      case 'audio':
        return <Music className="w-4 h-4 text-slate-500" />;
      case 'video':
        return <Video className="w-4 h-4 text-slate-500" />;
      default:
        return <File className="w-4 h-4 text-slate-500" />;
    }
  };

  // Render loading state
  if (carregando && passos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-700 border-t-transparent"></div>
      </div>
    );
  }

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------
  return (
    <div className={`min-h-screen w-full transition-colors duration-200 ${isDark
      ? 'bg-[#111827] text-gray-200'
      : 'bg-gradient-to-b from-gray-50 to-white text-gray-800'
      }`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header com logo e título */}
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Sequência de Follow-up
          </h1>

          <div className={`flex space-x-1 p-1 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <button
              onClick={() => setActiveTab('sequence')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'sequence'
                ? 'bg-sminy-primary text-white shadow-sm'
                : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
            >
              <MessageCircle className="w-4 h-4" />
              Mensagens
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'schedule'
                ? 'bg-sminy-primary text-white shadow-sm'
                : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
            >
              <Calendar className="w-4 h-4" />
              Horários
            </button>
          </div>
        </div>

        {/* Alertas */}
        {erro && (
          <div className="fixed bottom-6 right-6 py-3 px-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50 rounded-xl shadow-lg flex items-center max-w-md animate-in slide-in-from-bottom-2 z-50">
            <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
            {erro}
          </div>
        )}
        {sucesso && (
          <div className="fixed bottom-6 right-6 py-3 px-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900/50 rounded-xl shadow-lg flex items-center max-w-md animate-in slide-in-from-bottom-2 z-50">
            <Check className="w-5 h-5 mr-3 shrink-0" />
            {sucesso}
          </div>
        )}

        {/* Save status indicator */}
        {savedStatus && (
          <div className={`fixed bottom-6 right-6 py-3 px-4 rounded-xl shadow-lg flex items-center gap-2 border animate-in slide-in-from-bottom-2 z-50 ${savedStatus === 'saving' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50' :
            savedStatus === 'saved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-900/50' :
              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50'
            }`}>
            {savedStatus === 'saving' ?
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                <span className="font-medium">Salvando...</span>
              </> :
              savedStatus === 'saved' ?
                <>
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Salvo com sucesso!</span>
                </> :
                <>
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Erro ao salvar</span>
                </>
            }
          </div>
        )}

        {/* Sequence tab */}
        {activeTab === 'sequence' && (
          <div className="mb-8">
            {/* Visão de Overview (quando não está editando um passo) */}
            {!editandoPasso ? (
              <>
                <div className={`rounded-2xl shadow-sm p-6 mb-8 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Visão geral da sequência</h2>
                    <button
                      className={`rounded-xl px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${isDark
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      onClick={salvarConfig}
                      disabled={passos.length === 0 || carregando}
                    >
                      <Save className="w-4 h-4" />
                      Salvar alterações
                    </button>
                  </div>

                  {/* Timeline visualization */}
                  {passos.length > 0 ? (
                    <TimelineIndicator
                      passos={passos}
                      onEditStep={editarPasso}
                      onAddStep={adicionarPasso}
                    />
                  ) : (
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-slate-50 border-slate-200'
                      }`}>
                      <MessageCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                      <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Nenhum passo configurado. Crie sua sequência de follow-up.
                      </p>
                      <button
                        onClick={adicionarPasso}
                        className="px-4 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary/90 transition-colors flex items-center justify-center mx-auto"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar primeiro passo
                      </button>
                    </div>
                  )}
                </div>

                {/* Passos em cards */}
                {passos.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {passos.map((passo) => {
                      // Ordenar mensagens por ID
                      const messagesSorted = passo.messages
                        .slice()
                        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

                      return (
                        <div key={passo.step_number} className={`rounded-2xl shadow-sm overflow-hidden transition-all hover:shadow-md border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}>
                          <div className={`px-6 py-4 flex justify-between items-center border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-slate-50 border-slate-100'
                            }`}>
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-white bg-sminy-primary`}>
                                {passo.step_number}
                              </div>
                              <div>
                                <h3 className={`font-semibold ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>Passo {passo.step_number}</h3>
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                  Após {passo.send_after} {unidadeTempoTexto(passo.send_after_unit)}
                                  {passo.id && <span className="ml-2">(ID: {passo.id})</span>}
                                </p>
                              </div>
                            </div>

                            <button
                              className={`transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-800'}`}
                              onClick={() => editarPasso(passo.step_number)}
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                          </div>

                          <div className="p-6">
                            {messagesSorted.length > 0 ? (
                              messagesSorted.map((message, msgIndex) => (
                                <div key={msgIndex} className="mb-4 last:mb-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    {renderIconForMessageType(message.type)}
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                      Mensagem {msgIndex + 1} • {
                                        message.type === 'text' ? 'Texto' :
                                          message.type === 'template' ? 'Template Meta' :
                                          message.type === 'image' ? 'Imagem' :
                                            message.type === 'audio' ? 'Áudio' :
                                              message.type === 'video' ? 'Vídeo' :
                                                'Desconhecido'
                                      }
                                      {message.id && <span className="ml-1">(ID: {message.id})</span>}
                                    </span>
                                  </div>

                                  <MessagePreview message={message} />
                                </div>
                              ))
                            ) : (
                              <div className={`text-center py-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                                <MessageCircle className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-700' : 'text-slate-300'}`} />
                                <p>Nenhuma mensagem definida</p>
                              </div>
                            )}

                            {messagesSorted.length < MAX_MENSAGENS_POR_PASSO && (
                              <button
                                onClick={() => adicionarMensagem(passo.step_number)}
                                className={`w-full mt-4 py-2 border border-dashed rounded-xl flex items-center justify-center transition-colors ${isDark
                                  ? 'border-gray-600 text-gray-400 hover:bg-gray-700/50'
                                  : 'border-sminy-primary/30 text-sminy-primary hover:bg-sminy-primary/5'
                                  }`}
                              >
                                <Plus className="w-4 h-4 inline mr-1" />
                                Adicionar mensagem
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add new step card (if limit not reached) */}
                    {passos.length < MAX_PASSOS && (
                      <div className={`rounded-2xl border-2 border-dashed flex items-center justify-center h-64 ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-sminy-primary/5 border-sminy-primary/20'
                        }`}>
                        <button
                          onClick={adicionarPasso}
                          className={`font-medium flex flex-col items-center gap-2 transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-sminy-primary hover:text-sminy-primary/80'
                            }`}
                        >
                          <div className={`h-12 w-12 rounded-full border-2 border-current flex items-center justify-center`}>
                            <Plus className="w-6 h-6" />
                          </div>
                          <span>Adicionar Novo Passo</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Formulário de edição de passo */
              <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <div className={`px-6 py-4 flex justify-between items-center border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-slate-50 border-slate-100'
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-white bg-sminy-primary`}>
                      {editandoPasso.step_number}
                    </div>
                    <h3 className={`font-semibold ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                      Editar Passo {editandoPasso.step_number}
                      {editandoPasso.id && <span className={`text-xs ml-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>(ID: {editandoPasso.id})</span>}
                    </h3>
                  </div>

                  <button
                    className={`transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={fecharEdicaoPasso}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6">
                  {/* Timing configuration */}
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                      Tempo após o passo anterior
                    </label>
                    <div className="flex gap-3">
                      <div className="w-24">
                        <input
                          type="number"
                          min="1"
                          value={editandoPasso.send_after}
                          onChange={(e) => atualizarPasso({
                            ...editandoPasso,
                            send_after: parseInt(e.target.value || '1', 10),
                          })}
                          className={`w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-slate-900'
                            }`}
                        />
                      </div>
                      <div className="w-32">
                        <select
                          value={editandoPasso.send_after_unit}
                          onChange={(e) => atualizarPasso({
                            ...editandoPasso,
                            send_after_unit: e.target.value as 'days' | 'hours' | 'minutes',
                          })}
                          className={`w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-slate-900'
                            }`}
                        >
                          {timeUnits.map(unit => (
                            <option key={unit.value} value={unit.value}>{unit.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Mensagens</h4>
                      {editandoPasso.messages.length < MAX_MENSAGENS_POR_PASSO && (
                        <button
                          onClick={() => adicionarMensagem(editandoPasso.step_number)}
                          className={`text-sm flex items-center gap-1 transition-colors ${isDark ? 'text-sminy-primary hover:text-sminy-primary/80' : 'text-slate-600 hover:text-slate-800'
                            }`}
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar Mensagem
                        </button>
                      )}
                    </div>

                    {availableTemplates.length === 0 && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                        Nenhum template Meta compatível foi encontrado para `follow_up`. Use um template aprovado com BODY/HEADER textual em `/prompt/templates` para usar esse tipo de mensagem.
                      </div>
                    )}
                    {unsupportedTemplateCount > 0 && (
                      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 dark:border-sky-900/50 dark:bg-sky-900/20 dark:text-sky-200">
                        {unsupportedTemplateCount} template(s) foram ocultados por usarem componentes ainda não suportados nesta automação, como header de mídia ou botão dinâmico.
                      </div>
                    )}

                    {/* Mensagens existentes */}
                    {editandoPasso.messages.length > 0 ? (
                      // Garante que as mensagens são ordenadas por ID ao exibi-las no modo de edição
                      [...editandoPasso.messages]
                        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
                        .map((message, index) => (
                          <div key={index} className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-slate-50 border-slate-200'
                            }`}>
                            <div className="flex justify-between items-center mb-3">
                              <div className="flex items-center gap-2">
                                <span className={`rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-slate-200 text-slate-600'
                                  }`}>
                                  {index + 1}
                                </span>
                                <select
                                  value={message.type}
                                  onChange={(e) => alterarMensagem(
                                    editandoPasso.step_number,
                                    index,
                                    e.target.value as 'text' | 'image' | 'audio' | 'video' | 'template',
                                    (() => {
                                      const nextType = e.target.value as MensagemLocal['type'];
                                      if (nextType === 'text') {
                                        return message.type === 'text' && typeof message.content === 'string' ? message.content : '';
                                      }
                                      if (nextType === 'template') {
                                        if (message.type === 'template') {
                                          return normalizeTemplateContent(message.content);
                                        }
                                        return availableTemplates[0]
                                          ? buildTemplateMessageConfig(availableTemplates[0], 'follow_up')
                                          : {
                                            template_name: '',
                                            language: 'pt_BR',
                                            components: [],
                                          };
                                      }
                                      return '';
                                    })()
                                  )}
                                  className={`text-sm border-0 bg-transparent focus:ring-0 font-medium cursor-pointer ${isDark ? 'text-gray-200' : 'text-slate-700'
                                    }`}
                                >
                                  {messageTypes.map(type => (
                                    <option key={type.value} value={type.value} className={isDark ? 'bg-gray-800' : ''}>{type.label}</option>
                                  ))}
                                </select>
                              </div>
                              <button
                                className="text-red-500 hover:text-red-700 transition-colors"
                                onClick={() => deletarMensagem(editandoPasso.step_number, index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {message.type === 'text' ? (
                              <textarea
                                value={typeof message.content === 'string' ? message.content : ''}
                                onChange={(e) => alterarMensagem(
                                  editandoPasso.step_number,
                                  index,
                                  'text',
                                  e.target.value
                                )}
                                rows={3}
                                className={`w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary resize-none ${isDark
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-300 text-slate-900 placeholder-slate-400'
                                  }`}
                                placeholder="Digite sua mensagem aqui..."
                              />
                            ) : message.type === 'template' ? (
                              <div className="space-y-3">
                                <select
                                  value={normalizeTemplateContent(message.content)?.template_name || ''}
                                  onChange={(e) => alterarMensagem(
                                    editandoPasso.step_number,
                                    index,
                                    'template',
                                    (() => {
                                      const selectedTemplate = availableTemplates.find(
                                        (template) => template.name === e.target.value,
                                      );
                                      if (!selectedTemplate) {
                                        return {
                                          template_name: '',
                                          language: 'pt_BR',
                                          components: [],
                                        };
                                      }
                                      return buildTemplateMessageConfig(selectedTemplate, 'follow_up');
                                    })()
                                  )}
                                  className={`w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-slate-900'
                                    }`}
                                >
                                  <option value="">Selecione um template aprovado</option>
                                  {availableTemplates.map((template) => (
                                    <option key={template.id} value={template.name}>
                                      {template.name}
                                    </option>
                                  ))}
                                </select>

                                {(() => {
                                  const templateConfig = normalizeTemplateContent(message.content);
                                  const selectedTemplate = templateConfig?.template_name
                                    ? getTemplateByName(templateConfig.template_name)
                                    : undefined;
                                  const analysis = selectedTemplate
                                    ? analyzeTemplateSupport(selectedTemplate)
                                    : null;

                                  if (!templateConfig || !selectedTemplate) {
                                    return null;
                                  }

                                  return (
                                    <>
                                      <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-slate-200 bg-slate-50'}`}>
                                        <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                          Template selecionado
                                        </p>
                                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                                          {templateConfig.template_name}
                                        </p>
                                        {!!analysis?.bodyText && (
                                          <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                            {analysis.bodyText}
                                          </p>
                                        )}
                                      </div>

                                      {analysis && analysis.slots.length > 0 && (
                                        <div className="space-y-3">
                                          {analysis.slots.map((slot) => {
                                            const component = templateConfig.components.find(
                                              (item) => item.type.toUpperCase() === slot.componentType,
                                            );
                                            const parameter = component?.parameters?.[slot.parameterIndex] || {
                                              type: 'text' as const,
                                              source: 'literal',
                                              value: '',
                                            };

                                            return (
                                              <div
                                                key={slot.id}
                                                className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-slate-200 bg-white'}`}
                                              >
                                                <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                                  {slot.componentType} {slot.placeholder}
                                                </p>

                                                <select
                                                  value={parameter.source}
                                                  onChange={(e) => {
                                                    const nextConfig = normalizeTemplateContent(templateConfig, templateConfig.template_name);
                                                    if (!nextConfig) return;
                                                    const nextComponent = nextConfig.components.find(
                                                      (item) => item.type.toUpperCase() === slot.componentType,
                                                    );
                                                    if (!nextComponent) return;
                                                    nextComponent.parameters[slot.parameterIndex] = {
                                                      type: 'text',
                                                      source: e.target.value,
                                                      value: e.target.value === 'literal'
                                                        ? (nextComponent.parameters[slot.parameterIndex]?.value || '')
                                                        : undefined,
                                                    };
                                                    alterarMensagem(editandoPasso.step_number, index, 'template', nextConfig);
                                                  }}
                                                  className={`mt-2 w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white'
                                                    : 'bg-white border-gray-300 text-slate-900'
                                                    }`}
                                                >
                                                  {getTemplateVariableOptions('follow_up').map((option) => (
                                                    <option key={option.key} value={option.key}>
                                                      {option.label}
                                                    </option>
                                                  ))}
                                                </select>

                                                <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                                  {getTemplateVariableOption(parameter.source, 'follow_up')?.description || 'Selecione a origem do valor.'}
                                                </p>

                                                {parameter.source === 'literal' && (
                                                  <input
                                                    type="text"
                                                    value={parameter.value || ''}
                                                    onChange={(e) => {
                                                      const nextConfig = normalizeTemplateContent(templateConfig, templateConfig.template_name);
                                                      if (!nextConfig) return;
                                                      const nextComponent = nextConfig.components.find(
                                                        (item) => item.type.toUpperCase() === slot.componentType,
                                                      );
                                                      if (!nextComponent) return;
                                                      nextComponent.parameters[slot.parameterIndex] = {
                                                        type: 'text',
                                                        source: 'literal',
                                                        value: e.target.value,
                                                      };
                                                      alterarMensagem(editandoPasso.step_number, index, 'template', nextConfig);
                                                    }}
                                                    placeholder="Digite o texto fixo para este parametro"
                                                    className={`mt-2 w-full rounded-xl border text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                                                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                      : 'bg-white border-gray-300 text-slate-900 placeholder-slate-400'
                                                      }`}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div className="mt-2">
                                {/* Arquivo já enviado */}
                                {typeof message.content === 'string' && message.content && (
                                  <div className="mb-2">
                                    <p className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                      Arquivo atual: {message.content.split('/').pop()}
                                    </p>
                                    <PreviewArquivo tipo={message.type} conteudo={message.content} />
                                  </div>
                                )}

                                {/* Upload de arquivo */}
                                <div className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center text-center transition-colors ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-slate-300 bg-slate-50/50'
                                  }`}>
                                  {isFile(message.content) ? (
                                    <>
                                      <File className={`w-8 h-8 mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                                      <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{message.content.name}</p>
                                    </>
                                  ) : (
                                    <File className={`w-8 h-8 mb-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                                  )}

                                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                    {message.type === 'image' && `Máx ${LIMITE_ARQUIVOS.image / (1024 * 1024)}MB (imagens)`}
                                    {message.type === 'audio' && `Máx ${LIMITE_ARQUIVOS.audio / (1024 * 1024)}MB (áudios)`}
                                    {message.type === 'video' && `Máx ${LIMITE_ARQUIVOS.video / (1024 * 1024)}MB (vídeos)`}
                                  </p>

                                  <input
                                    type="file"
                                    id={`file-${editandoPasso.step_number}-${index}`}
                                    className="hidden"
                                    accept={
                                      message.type === 'image' ? 'image/*' :
                                        message.type === 'audio' ? 'audio/*' : 'video/*'
                                    }
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        alterarMensagem(
                                          editandoPasso.step_number,
                                          index,
                                          message.type,
                                          e.target.files[0]
                                        );
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`file-${editandoPasso.step_number}-${index}`}
                                    className={`text-xs py-1 px-3 rounded cursor-pointer transition-colors ${isDark ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                                      }`}
                                  >
                                    {isFile(message.content) ? 'Alterar arquivo' : 'Selecionar arquivo'}
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <div className={`text-center p-8 border-2 border-dashed rounded-lg ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-slate-200'
                        }`}>
                        <MessageCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                        <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Nenhuma mensagem adicionada para este passo
                        </p>
                        <button
                          onClick={() => adicionarMensagem(editandoPasso.step_number)}
                          className={`px-4 py-2 rounded-xl text-white transition-colors flex items-center justify-center mx-auto bg-sminy-primary hover:bg-sminy-primary/90`}
                        >
                          <Plus className="w-4 h-4 inline mr-2" />
                          Adicionar primeira mensagem
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-slate-100'}`}>
                    <button
                      className={`px-4 py-2 border rounded-xl transition-colors ${isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                      onClick={fecharEdicaoPasso}
                    >
                      Cancelar
                    </button>
                    <button
                      className={`px-4 py-2 text-white rounded-xl flex items-center gap-2 transition-colors bg-sminy-primary hover:bg-sminy-primary/90`}
                      onClick={salvarConfig}
                    >
                      <Save className="w-4 h-4" />
                      Salvar alterações
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Schedule tab */}
        {activeTab === 'schedule' && (
          <div className={`rounded-xl shadow-sm p-6 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Configuração de horários</h2>

              {scheduleExists && !scheduleEditMode ? (
                <div className="space-x-2">
                  <button
                    className={`rounded-xl px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${isDark
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    onClick={() => setScheduleEditMode(true)}
                  >
                    <Edit2 className="w-4 h-4" />
                    Editar horários
                  </button>
                </div>
              ) : (
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  onClick={handleSaveScheduleConfig}
                >
                  <Save className="w-4 h-4" />
                  Salvar horários
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 mb-8">
              {daysOfWeek.map(day => {
                const dayData = schedule[day.key] || { enabled: false, start: '09:00', end: '17:00' };

                return (
                  <div
                    key={day.key}
                    className={`rounded-xl p-4 border transition-colors ${dayData.enabled
                      ? isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-100 border-slate-200'
                      : isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-slate-50 border-slate-100'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>{day.label}</span>
                      {(scheduleEditMode || !scheduleExists) ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={dayData.enabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSchedule((prev) => ({
                                ...prev,
                                [day.key]: { ...prev[day.key], enabled: checked },
                              }));
                            }}
                            className="sr-only peer"
                          />
                          <div className={`w-9 h-5 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all ${isDark ? 'bg-gray-600 peer-checked:bg-sminy-primary' : 'bg-slate-200 peer-checked:bg-sminy-primary'
                            }`}></div>
                        </label>
                      ) : (
                        <div className={`w-3 h-3 rounded-full ${dayData.enabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-slate-300'}`}></div>
                      )}
                    </div>

                    {dayData.enabled ? (
                      <div className="text-center space-y-2">
                        <div className="flex items-center gap-2 justify-center">
                          <Clock className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                          {(scheduleEditMode || !scheduleExists) ? (
                            <input
                              type="time"
                              value={dayData.start}
                              onChange={(e) => {
                                setSchedule((prev) => ({
                                  ...prev,
                                  [day.key]: { ...prev[day.key], start: e.target.value },
                                }));
                              }}
                              className={`rounded text-sm p-1 border focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'border-slate-200 text-slate-700'
                                }`}
                            />
                          ) : (
                            <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>{dayData.start}</span>
                          )}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>até</div>
                        <div className="flex items-center gap-2 justify-center">
                          <Clock className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                          {(scheduleEditMode || !scheduleExists) ? (
                            <input
                              type="time"
                              value={dayData.end}
                              onChange={(e) => {
                                setSchedule((prev) => ({
                                  ...prev,
                                  [day.key]: { ...prev[day.key], end: e.target.value },
                                }));
                              }}
                              className={`rounded text-sm p-1 border focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'border-slate-200 text-slate-700'
                                }`}
                            />
                          ) : (
                            <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>{dayData.end}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center pt-2 pb-1">
                        <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>Desativado</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Schedule visualization */}
            <div className={`border rounded-xl overflow-hidden mt-8 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
              <div className={`px-4 py-3 border-b flex items-center ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
                <h3 className={`font-medium ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>Visualização da semana</h3>
              </div>

              <div className={`p-6 ${isDark ? 'bg-gray-900/30' : ''}`}>
                <div className="flex">
                  {/* Time indicators */}
                  <div className="w-16 pr-2 pt-6">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className={`h-8 text-xs text-right ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        {(i + 8) % 12 === 0 ? '12' : (i + 8) % 12}:00 {i + 8 < 12 ? 'AM' : 'PM'}
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  <div className="flex-1 grid grid-cols-7 gap-1">
                    {daysOfWeek.map(day => {
                      const dayData = schedule[day.key] || { enabled: false, start: '09:00', end: '17:00' };
                      let startHour = 0;
                      let endHour = 0;

                      if (dayData.enabled) {
                        const [startH] = dayData.start.split(':').map(Number);
                        const [endH] = dayData.end.split(':').map(Number);
                        startHour = startH;
                        endHour = endH;
                      }

                      // Calculate position
                      const startOffset = Math.max(0, startHour - 8) * 8;
                      const duration = Math.max(0, endHour - startHour) * 8;

                      return (
                        <div key={day.key} className="relative">
                          <div className={`h-6 text-xs font-medium text-center border-b pb-1 ${isDark ? 'text-gray-400 border-gray-700' : 'text-slate-600 border-slate-200'
                            }`}>
                            {day.label}
                          </div>

                          {/* Time blocks */}
                          <div className={`h-96 relative border-l ${isDark ? 'border-gray-800' : 'border-slate-100'}`}>
                            {Array.from({ length: 12 }).map((_, i) => (
                              <div key={i} className={`h-8 border-b ${isDark ? 'border-gray-800' : 'border-slate-100'}`}></div>
                            ))}

                            {dayData.enabled && (
                              <div
                                className={`absolute left-0 right-0 border-l-4 rounded-r-md px-2 ${isDark ? 'bg-sminy-primary/20 border-sminy-primary' : 'bg-sminy-primary/10 border-sminy-primary'
                                  }`}
                                style={{ top: `${startOffset}px`, height: `${duration}px` }}
                              >
                                <div className={`text-xs font-medium h-full flex items-center text-sminy-primary`}>
                                  <span>{dayData.start} - {dayData.end}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Botões de ação para configuração de horários */}
            <div className="flex justify-end mt-6 gap-3">
              {scheduleExists && (
                <>
                  {scheduleEditMode ? (
                    <button
                      className={`px-4 py-2 border rounded-xl transition-colors ${isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                      onClick={() => setScheduleEditMode(false)}
                    >
                      Cancelar
                    </button>
                  ) : (
                    <button
                      className={`px-4 py-2 border rounded-xl transition-colors ${isDark
                        ? 'border-red-900/50 text-red-400 hover:bg-red-900/20'
                        : 'border-red-300 text-red-700 hover:bg-red-50'
                        }`}
                      onClick={handleDeleteScheduleConfig}
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Excluir configuração
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUp;
